// Canonical range-pricing row. Wire shape is { from, to, unitCost } (camelCase);
// the server deserializes List<CostByRange>(From,To,UnitCost) with
// PropertyNameCaseInsensitive=true, so camelCase round-trips. RangeLookup treats
// [from, to] as INCLUSIVE, miss → last row's unitCost. `unitCost` is in DOLLARS.
export interface CostByRangeRow {
  from: number;
  to: number;
  unitCost: number;
}

export function parseCostByRange(json: string | null | undefined): CostByRangeRow[] {
  if (!json || !json.trim()) return [];
  try {
    const v: unknown = JSON.parse(json);
    if (!Array.isArray(v)) return [];
    return v.map((raw) => {
      const c = raw as Record<string, unknown>;
      return {
        from: Number(c.from) || 0,
        to: Number(c.to) || 0,
        unitCost: Number(c.unitCost) || 0,
      };
    });
  } catch {
    return [];
  }
}

export function serializeCostByRange(rows: CostByRangeRow[]): string {
  const sorted = [...rows].sort((a, b) => a.from - b.from);
  return JSON.stringify(sorted);
}

/** Per-row hard errors (block save). */
export function rangeRowErrors(row: CostByRangeRow): string[] {
  const errs: string[] = [];
  if (row.from > row.to) errs.push("from-after-to");
  if (row.from < 0 || row.to < 0 || row.unitCost < 0) errs.push("negative");
  return errs;
}

/** Soft, non-blocking warning. Convention: each range starts exactly where the
 *  previous ended (shared boundary = clean). next.from < prev.to → overlap;
 *  next.from > prev.to → gap. Returns null when clean or fewer than 2 rows. */
export function rangeGapOverlapWarning(rows: CostByRangeRow[]): string | null {
  if (rows.length < 2) return null;
  const sorted = [...rows].sort((a, b) => a.from - b.from);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]!;
    const cur = sorted[i]!;
    if (cur.from < prev.to) return "Ranges overlap — pricing may be ambiguous.";
    if (cur.from > prev.to) return "Ranges leave a gap — some quantities fall through.";
  }
  return null;
}
